/**
 * Stable Spaces lock ids per editable surface on an RFQ item. Receivers
 * pattern-match on the suffix to know which conflict UX to show.
 */
export const rfqRowLockId = (itemId: string) => `item:${itemId}:row`;
export const rfqCustomerNoteLockId = (itemId: string) =>
  `item:${itemId}:customerNote`;

/**
 * Per-field edit locks for a Quotes V2 product row. Each editable input
 * (`quantity`, `sell` = Unit Price, `gp` = GP$, `gpPercent` = GP%) locks
 * independently so the editor's avatar can show on the exact field they're on.
 * The three pricing fields (`sell`/`gp`/`gpPercent`) recalculate each other, so
 * they're DISABLED together when any one is held (see `usePricingLock`) — but
 * the avatar only appears on the field actually being edited. `note`, `label`
 * and `options` are dialog-scoped surfaces (Edit Note, Add/Edit Label, Add
 * Options) that each lock independently while a collaborator has that dialog
 * open. `remove` is held while a collaborator has the Remove confirmation dialog
 * open, so others can't also remove the same item.
 */
export type QuoteItemField =
  | 'quantity'
  | 'sell'
  | 'gp'
  | 'gpPercent'
  | 'note'
  | 'label'
  | 'options'
  | 'remove';
export const quoteItemFieldLockId = (itemId: string, field: QuoteItemField) =>
  `quoteItem:${itemId}:${field}`;

/**
 * The pricing inputs that lock as a group (disable together) but each show the
 * editor's avatar only on their own field.
 */
export const QUOTE_PRICING_FIELDS = ['sell', 'gp', 'gpPercent'] as const;

/**
 * Section-level lock for a Quotes V2 section row. Held while a collaborator has
 * any of the section's actions open (Edit Section Title & Note, Add Items,
 * Move Existing Items, Remove Section …) so others see the whole section action
 * menu disabled until they're done.
 */
export const quoteSectionLockId = (sectionId: string) =>
  `quoteSection:${sectionId}`;
